import { useUpdateUserOnboarding, useUserOnboarding } from "@/api/onboarding";
import type {
  UserOnboarding,
  UserOnboardingStatus,
} from "@cohesive/api-client";
import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import type { Stage } from "./types";

export const QUERY_KEY = ["userOnboarding"] as const;

export function deriveStage(
  record: UserOnboarding | undefined,
  loading: boolean,
): Stage {
  if (loading || !record) return "unresolved";
  const global = record.global ?? "";
  if (global === "complete") return "done";
  if (global === "skipped") return "skipped";
  if (global === "in_progress") return "started";
  return "new";
}

export interface StatusWriter {
  finish: () => Promise<boolean>;
  skip: () => Promise<boolean>;
  /**
   * Any other field of the record. `steps` and `refs` merge per key server
   * side, so a patch carries only the keys it changes; an empty value deletes
   * one.
   */
  patch: (body: UserOnboarding) => Promise<boolean>;
}

// The last write's response wins the cache; an earlier response landing late
// must not roll a newer record back.
let sequence = 0;
// A response carries the record as the server had it for that one write, which
// need not include a write still on the wire beside it. Every patch of a round
// of overlapping writes is replayed over whichever response the cache takes;
// replaying one the response already holds changes nothing.
const overlapping = new Map<number, UserOnboarding>();
let unsettled = 0;
// A refused write leaves its optimistic apply behind, and only the API can say
// what belongs there instead. The refetch waits for the round to empty: a GET
// sent beside a write still on the wire can answer from before it and land
// after it, rewinding the tour a step.
let needsRefetch = false;

const FIRST_RUN_RANK: Record<string, number> = {
  started: 1,
  sent: 2,
  landed: 3,
};

/**
 * The local mirror of the API's merge: scalars replace, maps merge per key,
 * and `first_run` merges per field with a status that only moves forward
 * (`null` clears it). The server stamps the timestamps; until its response
 * lands the mirror has none to show.
 */
export function applyPatch(
  current: UserOnboarding,
  patch: UserOnboarding,
): UserOnboarding {
  const next: UserOnboarding = { ...current, ...patch };
  if (patch.first_run === undefined) {
    next.first_run = current.first_run;
  } else if (patch.first_run !== null) {
    const was = current.first_run ?? {};
    const merged = { ...was, ...patch.first_run };
    const from = FIRST_RUN_RANK[was.status ?? ""] ?? 0;
    const to = FIRST_RUN_RANK[patch.first_run.status ?? ""] ?? 0;
    if (to < from) merged.status = was.status;
    next.first_run = merged;
  }
  for (const field of ["steps", "refs"] as const) {
    const incoming = patch[field];
    if (!incoming) {
      next[field] = current[field];
      continue;
    }
    const merged = { ...(current[field] ?? {}) };
    for (const [key, value] of Object.entries(incoming)) {
      if (value === "") delete merged[key];
      else merged[key] = value;
    }
    next[field] = merged;
  }
  return next;
}

/**
 * The only writer of the onboarding record. Every write shows at once in the
 * cache and is reconciled with the response.
 */
export function createStatusWriter({
  queryClient,
  update,
}: {
  queryClient: QueryClient;
  update: (body: UserOnboarding) => Promise<UserOnboarding>;
}): StatusWriter {
  const write = async (patch: UserOnboarding): Promise<boolean> => {
    const current = queryClient.getQueryData<UserOnboarding>(QUERY_KEY);
    if (!current) return false;
    const seq = ++sequence;
    overlapping.set(seq, patch);
    unsettled += 1;
    queryClient.setQueryData<UserOnboarding>(
      QUERY_KEY,
      applyPatch(current, patch),
    );
    try {
      const response = await update(patch);
      if (seq === sequence) {
        let next = response;
        for (const other of overlapping.values())
          next = applyPatch(next, other);
        queryClient.setQueryData(QUERY_KEY, next);
      }
      return true;
    } catch {
      // Out of the round before any sibling's response can replay it: the row
      // never took this patch, so the cache must not keep it either.
      overlapping.delete(seq);
      needsRefetch = true;
      return false;
    } finally {
      unsettled -= 1;
      if (unsettled === 0) {
        overlapping.clear();
        if (needsRefetch) {
          needsRefetch = false;
          void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
        }
      }
    }
  };
  const status = (global: UserOnboardingStatus) => () => write({ global });
  return {
    finish: status("complete"),
    skip: status("skipped"),
    patch: write,
  };
}

export function useOnboardingStatus(): StatusWriter {
  const queryClient = useQueryClient();
  const { mutateAsync } = useUpdateUserOnboarding();
  return useMemo(
    () => createStatusWriter({ queryClient, update: mutateAsync }),
    [mutateAsync, queryClient],
  );
}

/** The record, and what it says about where this user is. */
export function useOnboardingRecord(): {
  record: UserOnboarding | undefined;
  stage: Stage;
} {
  const { data, isLoading } = useUserOnboarding({ enabled: true });
  return { record: data, stage: deriveStage(data, isLoading) };
}
